import { useCallback } from 'react';
import Button from 'components/button';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import UseContext from 'hooks/useContext';
import {
  COMMENT_COUNTER_FROM,
  COMMENT_MAX_LENGTH,
  authorInitialsOf,
  authorNameOf,
  commentLengthOf,
  formatCommentAt,
  isCommentSendable,
  sendErrorText,
} from './invoiceCommentText';

const CommentRow = ({ comment, text }) => (
  <li className="flex gap-3 rounded-lg border border-gray-100 px-3 py-3">
    <div
      aria-hidden
      className="flex flex-shrink-0 items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-xs font-medium text-gray-700"
    >
      {authorInitialsOf(comment.author)}
    </div>
    <div className="flex flex-col min-w-0">
      <div className="text-sm">
        <span className="font-medium text-gray-900">
          {authorNameOf(text, comment.author)}
        </span>
        <span className="ml-2 text-gray-500">
          {formatCommentAt(text, comment.createdAt)}
        </span>
      </div>
      {/* A text node, never markup: React escapes it, pre-wrap keeps its line breaks. */}
      <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
        {comment.text}
      </p>
    </div>
  </li>
);

const CommentForm = ({ comments, text }) => {
  const { draft, changeDraft, send, isSending, sendFailure } = comments;
  const length = commentLengthOf(draft);
  const isTooLong = length > COMMENT_MAX_LENGTH;
  const canSend = isCommentSendable(draft) && !isSending;
  return (
    <div className="flex flex-col gap-2">
      <textarea
        aria-label={text('inputLabel', 'Votre commentaire')}
        placeholder={text('placeholder', 'Écrire un commentaire…')}
        value={draft}
        readOnly={isSending}
        rows={3}
        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-500"
        onChange={(event) => changeDraft(event.target.value)}
      />
      {length >= COMMENT_COUNTER_FROM && (
        <div
          aria-live="polite"
          className={`text-xs text-right ${isTooLong ? 'text-red-600' : 'text-gray-500'}`}
        >
          {fillText(text('counter', '{count} / {max}'), {
            count: length,
            max: COMMENT_MAX_LENGTH,
          })}
          {isTooLong && (
            <span className="ml-2">
              {text('tooLong', 'Un commentaire fait au plus 2 000 caractères.')}
            </span>
          )}
        </div>
      )}
      {sendFailure && (
        <div role="alert" className="text-sm text-red-600">
          {sendErrorText(text, sendFailure)}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          color={canSend ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSend}
          aria-busy={isSending || undefined}
          onClick={send}
        >
          {text('submit', 'Commenter')}
        </Button>
      </div>
    </div>
  );
};

/**
 * The "Commentaires" tab of an invoice's detail (JBWEB-1279): notes the admins
 * write to each other, oldest first. They live in Jobbiz only (api decision
 * 58), and are neither edited nor deleted: a mistake is fixed by one more
 * comment. `comments` comes from `useInvoiceComments`, held by the panel.
 */
const InvoiceCommentsTab = ({ comments }) => {
  const { language } = UseContext();
  const text = useCallback(
    (key, fallback) => language.getValue(`invoiceComments.${key}`, fallback),
    [language],
  );

  if (comments.failure) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-sm text-gray-700"
      >
        <div>
          {comments.failure === 'notFound'
            ? text(
                'notFound',
                'Cette facture est introuvable : elle n’a pas de commentaires.',
              )
            : text('loadFailed', 'Les commentaires n’ont pas pu être chargés.')}
        </div>
        {comments.failure === 'failed' && (
          <Button color="SECONDARY" onClick={comments.reload}>
            {text('retryLoad', 'Réessayer')}
          </Button>
        )}
      </div>
    );
  }
  if (!comments.items) {
    return (
      <div role="status" className="py-16 text-center text-sm text-gray-500">
        {text('loading', 'Chargement des commentaires…')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {comments.items.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500">
          {text('empty', 'Aucun commentaire')}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {comments.items.map((comment) => (
            <CommentRow key={comment.id} comment={comment} text={text} />
          ))}
        </ul>
      )}
      <CommentForm comments={comments} text={text} />
    </div>
  );
};

export default InvoiceCommentsTab;
